import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import sbeeLogo from "../../../src/assets/sbeelogo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PURCHASE_API } from "../../apiConfig";

const CAL = "Calibri, 'Segoe UI', Arial, sans-serif";
const TNR = "'Times New Roman', Times, serif";

const fmtDate = (d) => {
    if (!d) return "";
    const s = String(d);
    const raw = s.includes("T") ? s.substring(0, 10) : s.substring(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return String(d);
    const [y, m, day] = raw.split("-");
    return `${day}/${m}/${y}`;
};

const fmtTime = (t) => {
    if (!t) return "";
    return String(t).substring(0, 5);
};

/* ════════════════════════════════════════════════════════════════ */
const JobOrder = () => {
    const { orderNo } = useParams();
    const [searchParams] = useSearchParams();

    // Now storing an array of Job Orders
    const [jobOrdersData, setJobOrdersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    /* ── Fetch all data for the job order ─────────────────────── */
    useEffect(() => {
        if (!orderNo) {
            setError("No Production Order number in URL.");
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const confirmationId = searchParams.get("confirmationId");
                const mode = searchParams.get("mode") === "single" ? "single" : "consolidated";

                const params = new URLSearchParams();
                if (confirmationId) params.set("confirmationId", confirmationId);
                params.set("mode", mode);

                const res = await fetch(
                    `${PURCHASE_API}/production-order/job-order/${orderNo}?${params.toString()}`
                );
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load order");

                if (!json.jobOrders || json.jobOrders.length === 0) {
                    setError("No Job Order data found.");
                    setLoading(false);
                    return;
                }

                // Process EACH job order in the array separately
                const processedOrders = json.jobOrders.map((orderData) => {
                    const allRows = orderData.data || [];
                    const header = allRows[0] || {};

                    /* Build routing rows — group by process_id + sub_process_id */
                    const grouped = [];
                    allRows.forEach((row) => {
                        const existing = grouped.find(
                            (g) =>
                                g.process_id === row.process_id &&
                                g.sub_process_id === row.sub_process_id
                        );
                        const act = {
                            activity_type: row.activity_type,
                            activity_value: row.activity_value,
                            activity_unit: row.activity_unit,
                            actual_activity_value: row.actual_activity_value,
                            idle_time_flag: row.idle_time_flag,
                            idle_time_reason: row.idle_time_reason,
                            idle_time_value: row.idle_time_value,
                            // Per-activity actual dates (e.g. Setup's own start/end,
                            // distinct from Run Time's) — each raw row here is one
                            // activity, so these mirror that activity specifically.
                            actual_start_date: row.actual_start_date || "",
                            actual_start_time: row.actual_start_time || "",
                            actual_end_date: row.actual_end_date || "",
                            actual_end_time: row.actual_end_time || "",
                        };
                        if (existing) {
                            const dup = existing.activities.some(
                                (a) =>
                                    a.activity_type === row.activity_type &&
                                    a.activity_value === row.activity_value
                            );
                            if (!dup && row.activity_type) existing.activities.push(act);
                        } else {
                            grouped.push({
                                process_id: row.process_id || "",
                                process_name: row.process_name || "",
                                sub_process_id: row.sub_process_id || "",
                                sub_process_name: row.sub_process_name || "",
                                workcenter_name: row.workcenter_name || "",
                                machine: row.machine || "",
                                start_date: row.start_date || "",
                                start_time: row.start_time || "",
                                end_date: row.end_date || "",
                                end_time: row.end_time || "",
                                // Actuals — previously not captured here at all, so the
                                // "Actual" Machine Time block had nothing to show no
                                // matter what had been entered during confirmation.
                                actual_start_date: row.actual_start_date || "",
                                actual_start_time: row.actual_start_time || "",
                                actual_end_date: row.actual_end_date || "",
                                actual_end_time: row.actual_end_time || "",
                                activities: row.activity_type ? [act] : [],
                            });
                        }
                    });

                    return {
                        ...orderData,
                        header,
                        routingRows: grouped.filter((r) => r.process_id),
                    };
                });

                setJobOrdersData(processedOrders);
                setLoading(false);
            } catch (err) {
                setError(err.message || "Error loading job order.");
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderNo, searchParams.get("mode")]);

    /* ── Auto download / print after data loads ──────────────── */
    useEffect(() => {
        if (jobOrdersData.length === 0) return;
        if (searchParams.get("download") === "true") {
            setTimeout(() => handleDownloadPDF(), 800);
        }
        if (searchParams.get("print") === "true") {
            setTimeout(() => window.print(), 800);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobOrdersData]);

    /* ── PDF Download ─────────────────────────────────────────── */
    const handleDownloadPDF = async () => {
        if (isDownloading) return; // guard against double-click firing two PDFs at once
        setIsDownloading(true);
        try {
            // Make sure fonts + the logo image are fully loaded before html2canvas
            // snapshots anything — a half-loaded image is a common reason the
            // canvas silently comes out blank or the capture throws.
            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }
            await Promise.all(
                Array.from(document.images).map((img) =>
                    img.complete
                        ? Promise.resolve()
                        : new Promise((resolve) => {
                              img.onload = resolve;
                              img.onerror = resolve; // don't let one broken image hang the whole export
                          })
                )
            );

            const A4_W_MM = 210;
            const A4_H_MM = 297;
            const MARGIN_MM = 10;
            const CONTENT_W = A4_W_MM - MARGIN_MM * 2;
            const CONTENT_H = A4_H_MM - MARGIN_MM * 2;

            const pdf = new jsPDF("p", "mm", "a4");
            const scale = 2; // Reduced slightly for memory safety on multi-page PDFs

            let cursorY = MARGIN_MM;
            let firstPage = true;
            let sectionsCaptured = 0;

            const canvasOpts = {
                useCORS: true,
                allowTaint: true,
                scale,
                backgroundColor: "#ffffff",
                logging: false,
                imageTimeout: 15000,
                scrollX: 0,
                scrollY: -window.scrollY,
            };

            // Iterate through EACH job order page container
            for (let i = 0; i < jobOrdersData.length; i++) {
                const sectionIds = [
                    `pdf-section-header-${i}`,
                    `pdf-section-machine-time-${i}`,
                    `pdf-section-material-${i}`,
                    `pdf-section-process-${i}`,
                    `pdf-section-machine-params-${i}`,
                    `pdf-section-inspection-${i}`,
                    `pdf-section-special-remarks-${i}`,
                    `pdf-section-downtime-${i}`,
                    `pdf-section-footer-${i}`,
                ];

                // Force a new PDF page for every new Job Order (except the very first one)
                if (!firstPage) {
                    pdf.addPage();
                    cursorY = MARGIN_MM;
                }

                for (const id of sectionIds) {
                    const el = document.getElementById(id);
                    if (!el) continue;

                    let canvas;
                    try {
                        canvas = await html2canvas(el, canvasOpts);
                    } catch (sectionErr) {
                        // Don't let one bad section (e.g. a tainted image) kill the
                        // whole export — log it, skip it, and keep going.
                        console.error(`PDF section "${id}" failed to render:`, sectionErr);
                        continue;
                    }

                    // A zero-size canvas produces an empty/corrupt data URL, which is
                    // exactly what triggers jsPDF's "wrong PNG signature" error later.
                    if (!canvas || canvas.width === 0 || canvas.height === 0) {
                        console.error(`PDF section "${id}" rendered an empty canvas — skipping.`);
                        continue;
                    }

                    sectionsCaptured++;
                    const sectionH_MM = (canvas.height * CONTENT_W) / canvas.width;

                    if (!firstPage && cursorY + sectionH_MM > A4_H_MM - MARGIN_MM && id !== sectionIds[0]) {
                        pdf.addPage();
                        cursorY = MARGIN_MM;
                    }

                    if (sectionH_MM > CONTENT_H) {
                        let sliceY = 0;
                        while (sliceY < canvas.height) {
                            if (!firstPage || sliceY > 0) {
                                pdf.addPage();
                                cursorY = MARGIN_MM;
                            }
                            const pxPerPage = (CONTENT_H * canvas.width) / CONTENT_W;
                            const sliceH = Math.min(pxPerPage, canvas.height - sliceY);

                            if (sliceH > 0) {
                                const sliceCanvas = document.createElement("canvas");
                                sliceCanvas.width = canvas.width;
                                sliceCanvas.height = sliceH;
                                sliceCanvas.getContext("2d").drawImage(
                                    canvas, 0, sliceY, canvas.width, sliceH,
                                    0, 0, canvas.width, sliceH
                                );
                                const sliceH_MM = (sliceH * CONTENT_W) / canvas.width;
                                try {
                                    // JPEG instead of PNG — smaller, and far more
                                    // tolerant of large-canvas encoding edge cases
                                    // that were producing malformed PNG headers.
                                    const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
                                    pdf.addImage(sliceData, "JPEG", MARGIN_MM, cursorY, CONTENT_W, sliceH_MM);
                                    cursorY += sliceH_MM;
                                } catch (imgErr) {
                                    console.error(`PDF slice of "${id}" failed to embed — skipping.`, imgErr);
                                }
                            }
                            sliceY += pxPerPage;
                            firstPage = false;
                        }
                    } else {
                        try {
                            const sectionData = canvas.toDataURL("image/jpeg", 0.92);
                            pdf.addImage(sectionData, "JPEG", MARGIN_MM, cursorY, CONTENT_W, sectionH_MM);
                            cursorY += sectionH_MM + 2;
                        } catch (imgErr) {
                            console.error(`PDF section "${id}" failed to embed — skipping.`, imgErr);
                        }
                        firstPage = false;
                    }
                }
            }

            if (sectionsCaptured === 0) {
                throw new Error("No sections could be captured — the job order content may not have rendered yet.");
            }

            pdf.save(`JobOrders_${orderNo}.pdf`);
        } catch (err) {
            console.error("PDF download error:", err);
            alert(
                `Sorry, the PDF could not be generated (${err.message || "unknown error"}). ` +
                `You can use the Print button and choose "Save as PDF" instead.`
            );
        } finally {
            setIsDownloading(false);
        }
    };

    /* ── Loading / Error states ───────────────────────────────── */
    if (loading) {
        return (
            <div style={{ fontFamily: CAL, fontSize: 17, padding: 60, textAlign: "center", color: "#475569" }}>
                Loading Job Order…
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ fontFamily: CAL, padding: 60, textAlign: "center" }}>
                <div style={{ fontSize: 46, marginBottom: 16 }}>🔒</div>
                <div style={{ fontSize: 17, color: "#dc2626", marginBottom: 10 }}>
                    Cannot Display Job Order
                </div>
                <div style={{ fontSize: 13, color: "#475569", maxWidth: 480, margin: "0 auto" }}>{error}</div>
            </div>
        );
    }

    /* ── Style constants ──────────────────────────────────────── */
    const border = "1px solid #000";

    const th = {
        border, padding: "8px 11px", background: "#f0f0f0", fontFamily: CAL, fontSize: 13, color: "#000", textAlign: "left", whiteSpace: "nowrap",
    };

    const td = {
        border, padding: "16px 11px", fontFamily: CAL, fontSize: 13, color: "#111", verticalAlign: "middle", minHeight: 44, height: 44, boxSizing: "border-box",
    };

    const tdBlank = {
        ...td, background: "#fffbeb", minHeight: 30, minWidth: 80,
    };

    const SectionTitle = ({ num, title }) => (
        <div style={{ background: "#1e293b", color: "#fff", fontFamily: CAL, fontSize: 14, padding: "6px 14px", marginTop: 22, marginBottom: 10, letterSpacing: 0.4, borderRadius: 2 }}>
            {num}. {title}
        </div>
    );

    const BlankCell = () => <td style={tdBlank}><div style={{ height: 34 }} /></td>;

    return (
        <div style={{ background: "#f3f4f6", padding: "30px 16px", minHeight: "100vh" }}>

            {/* ── Action bar (hidden on print) ──────────────────── */}
            <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end", maxWidth: 1120, margin: "0 auto 20px auto" }}>
                <span
                    style={{
                        marginRight: "auto",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        padding: "6px 12px",
                        borderRadius: 4,
                        color: searchParams.get("mode") === "single" ? "#92400e" : "#1e3a8a",
                        background: searchParams.get("mode") === "single" ? "#fef3c7" : "#dbeafe",
                    }}
                >
                    {searchParams.get("mode") === "single"
                        ? "FG JOB ORDER ONLY"
                        : `CONSOLIDATED JOB ORDER${jobOrdersData.length > 1 ? ` (${jobOrdersData.length} orders)` : ""}`}
                </span>
                <button
                    onClick={handleDownloadPDF}
                    disabled={isDownloading}
                    style={{ padding: "9px 22px", background: isDownloading ? "#64748b" : "#1e293b", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: isDownloading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}
                >
                    {isDownloading ? "Generating…" : "⬇ Download PDF"}
                </button>
                <button onClick={() => window.print()} style={{ padding: "9px 22px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                    🖨 Print
                </button>
                <button onClick={() => window.close()} style={{ padding: "9px 22px", background: "#64748b", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                    ✕ Close
                </button>
            </div>

            {/* Render a separate container for EACH Job Order */}
            {jobOrdersData.map((data, index) => {
                const {
                    header,
                    bomRows = [],
                    routingRows = [],
                    processParameters = [],
                    machineParameters = [],
                    inspectionParameters = [],
                    outputAttributes = [],
                    characteristics = [],
                    componentAttributes = {},
                    salesOrders = [],
                    specialRemarks = "",
                    hasScrapGroupLogic = false,
                    scrapHeadings = [],
                    outputGroupActual = null,
                    outputGroupRows = [],
                    outputGroupPlannedRows = [],
                } = data;

                // Scrap headings come from Scrap Configuration (matched by the
                // order's process). With none configured, the three legacy
                // columns are shown instead.
                const dynamicScrap = scrapHeadings.length > 0;
                const scrapCols = dynamicScrap
                    ? scrapHeadings.map(h => ({ key: h.scrap_name, label: h.scrap_name }))
                    : [{ key: "setup_scrap", label: "Setup Scrap" }, { key: "process_scrap", label: "Pro. Scrap" }, { key: "wirebreak_scrap", label: "WB. Scrap" }];
                const scrapVal = (r, k) => {
                    const v = dynamicScrap ? (r.scraps || {})[k] : r[k];
                    return v !== undefined && v !== null && v !== "" ? v : "";
                };

                // Dynamic bobbin rows — one row per bobbin, same shape as the
                // Confirmation screen. Falls back to the single legacy
                // outputGroupActual row for older data that hasn't been
                // re-confirmed under the new per-bobbin schema yet.
                // This is the ACTUAL side (filled in at Confirmation).
                const bobbinRows = outputGroupRows.length
                    ? outputGroupRows
                    : (outputGroupActual ? [outputGroupActual] : []);

                // PLANNED side — the bobbin rows entered on the Production
                // Order itself (No. of Bobbins / Length / Weight / Bobbin No).
                const plannedBobbinRows = outputGroupPlannedRows || [];

                // Before any Actual data has been confirmed, default the
                // Actual bobbin block to one row per planned bobbin — sum of
                // "No. of Bobbins" across the Planned rows (e.g. 3 + 6 = 9),
                // falling back to a single row when nothing is planned.
                const defaultBobbinRowCount = plannedBobbinRows.reduce(
                    (sum, g) => sum + (parseInt(g?.no_of_bobbins, 10) || 0), 0
                ) || 1;
                const displayBobbinRows = bobbinRows.length
                    ? bobbinRows
                    : Array.from({ length: defaultBobbinRowCount }, () => ({}));

                return (
                    <div
                        key={index}
                        id={`job-order-print-body-${index}`}
                        className="job-order-page"
                        style={{
                            background: "#fff",
                            padding: "40px 50px",
                            maxWidth: 1120,
                            margin: "0 auto 40px auto",
                            border: "8px solid #000",
                            fontFamily: CAL,
                            fontSize: 13,
                            color: "#000",
                            lineHeight: 1.5,
                        }}
                    >
                        {/* ══ pdf-section-header ══ */}
                        <div id={`pdf-section-header-${index}`}>
                            <div style={{ textAlign: "center", marginBottom: 14 }}>
                                <img src={sbeeLogo} alt="SBEE" crossOrigin="anonymous" style={{ height: 60, marginBottom: 8 }} />
                                <div style={{ fontSize: 12, lineHeight: 1.4 }}>
                                    SBEE CABLES INDIA LTD <br />
                                    No.282 &amp; 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar 562112, Karnataka, India
                                </div>
                            </div>

                            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
                                <tbody>
                                    <tr>
                                        <td style={{ border, padding: "10px 16px", width: "50%" }}>
                                            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>Job Order</div>
                                            <div style={{ fontSize: 19 }}>{header.production_order_number}</div>
                                        </td>
                                        {searchParams.get("confirmationId") && (
                                            <td style={{ border, padding: "10px 16px", width: "50%", textAlign: "right" }}>
                                                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", marginBottom: 4 }}>Confirmation ID</div>
                                                <div style={{ fontSize: 19 }}>{searchParams.get("confirmationId")}</div>
                                            </td>
                                        )}
                                    </tr>
                                </tbody>
                            </table>

                            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
                                <tbody>
                                    <tr>
                                        <td style={{ border, padding: "8px 12px", background: "#f0f4f8" }}>
                                            <div style={{ fontSize: 21 }}>
                                                {[...new Set(routingRows.map((r) => r.process_name).filter(Boolean))].join(", ") || ""}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0, tableLayout: "auto" }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, width: "1%", whiteSpace: "nowrap" }}>Sales Order</th>
                                        <th style={{ ...th, width: "1%", whiteSpace: "nowrap" }}>Product Code</th>
                                        <th style={{ ...th, whiteSpace: "normal" }}>Description</th>
                                        <th style={{ ...th, width: "1%", whiteSpace: "nowrap" }}>Req. Date</th>
                                        <th style={{ ...th, width: "1%", textAlign: "right", whiteSpace: "nowrap" }}>Qty</th>
                                        {(() => {
                                            const soRowsPreview = salesOrders.length > 0 ? salesOrders : [{ characteristics }];
                                            const headerChars = soRowsPreview.find((r) => r.characteristics && r.characteristics.length)?.characteristics || characteristics;
                                            // For Semi Finished Good outputs, the Sales Order's packing/drum
                                            // characteristics don't apply (packing happens at the FG stage) —
                                            // hide them from the Job Order printout.
                                            const isSemiFinishedGood = (header.productType || header.product_type || "").toLowerCase().includes("semi");
                                            const hiddenCharNames = ["no. of drum/coil", "length of drum/coil", "length of the drum/coil", "type of packing"];
                                            const visibleHeaderChars = isSemiFinishedGood
                                                ? headerChars.filter((c) => !hiddenCharNames.includes((c.characteristic || "").trim().toLowerCase()))
                                                : headerChars;
                                            // With tableLayout:"auto" the browser sizes every column to fit
                                            // its own content/header instead of a pre-computed percentage, so
                                            // Sales Order / Product Code / Tolerance Applied etc. shrink to
                                            // their content and Description simply takes whatever is left.
                                            return visibleHeaderChars.map((c, i) => (
                                                <th key={"soqc-" + i} style={{ ...th, width: "1%", fontSize: 10, textAlign: "center", background: "#e8f0fe", whiteSpace: "nowrap", padding: "8px 6px" }}>{c.characteristic}</th>
                                            ));
                                        })()}
                                        <th style={{ ...th, width: "1%", whiteSpace: "nowrap" }}>Tolerance Applied</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const soRows = salesOrders.length > 0 ? salesOrders : [{
                                            sales_order_id: header.sales_order_id || header.salesOrderId,
                                            customer_name: header.customer_name || header.customerName,
                                            customer_po_ref: header.customer_po_ref || header.customerPoRef,
                                            product_code: header.product_code || header.productCode,
                                            product_description: header.product_description || header.productDescription,
                                            requirement_date: header.requirement_date,
                                            so_qty: header.so_qty || header.soQty || header.general_qty,
                                            toleranceApplied: header.toleranceApplied || null,
                                            characteristics,
                                        }];
                                        const headerChars = soRows.find((r) => r.characteristics && r.characteristics.length)?.characteristics || characteristics;
                                        // Keep in sync with the header row above: hide the SO packing/drum
                                        // characteristics when the Job Order's output is a Semi Finished Good.
                                        const isSemiFinishedGood = (header.productType || header.product_type || "").toLowerCase().includes("semi");
                                        const hiddenCharNames = ["no. of drum/coil", "length of drum/coil", "length of the drum/coil", "type of packing"];
                                        const visibleHeaderChars = isSemiFinishedGood
                                            ? headerChars.filter((c) => !hiddenCharNames.includes((c.characteristic || "").trim().toLowerCase()))
                                            : headerChars;

                                        return soRows.map((row, i) => {
                                            // Each SO line has its own characteristics (e.g. No. of Drum/Coil,
                                            // Length of Drum/Coil) resolved server-side by so_id + item_no.
                                            // Match by name first (handles reordering), fall back to the
                                            // same position in this row's own list, never to another row's.
                                            const rowChars = (row.characteristics && row.characteristics.length) ? row.characteristics : null;
                                            return (
                                                <tr key={row.id || i}>
                                                    <td style={{ ...td, whiteSpace: "nowrap" }}>{row.sales_order_id || ""}</td>
                                                    <td style={{ ...td, whiteSpace: "nowrap" }}>{row.product_code || ""}</td>
                                                    <td style={{ ...td, wordBreak: "break-word" }}>{row.product_description || ""}</td>
                                                    <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDate(row.requirement_date)}</td>
                                                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{row.so_qty || ""}</td>
                                                    {visibleHeaderChars.map((c, ci) => {
                                                        let value = "";
                                                        if (rowChars) {
                                                            const byName = rowChars.find((rc) => rc.characteristic === c.characteristic);
                                                            value = (byName ? byName.value : rowChars[ci]?.value) || "";
                                                        }
                                                        return (
                                                            <td key={"soqcv-" + ci} style={{ ...td, textAlign: "center", fontSize: 11, background: "#f0f4ff", padding: "8px 6px", whiteSpace: "nowrap" }}>{value}</td>
                                                        );
                                                    })}
                                                    <td style={{ ...td, whiteSpace: "nowrap" }}>{row.toleranceApplied || ""}</td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>

                            {/* <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Construction</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Code</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Cable Type</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Category</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Cores</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Color</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Std. Ref</th>
                                        <th style={{ ...th, fontSize: 11, textAlign: "center" }}>Voltage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ ...td, textAlign: "center" }}>{header.construction || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.code || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.cableType || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.productCategory || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.noOfCores || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.colour || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.referenceOfStandard || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>{header.voltage || ""}</td>
                                    </tr>
                                </tbody>
                            </table> */}

                            {/* Product Code / Tolerance & Qty Conversion — merged into a single
                                table. UOM, Order Qty, and the per-SO characteristic columns
                                (No. of Drum/Coil, Length of the Drum/Coil, Over/Under Tolerance,
                                Type of Packing) were dropped from here; Qty in M / Qty in Kg are
                                derived server-side (see po_header.qty_in_m / qty_in_kg) from
                                productdetails.conversionLengthM / conversionWeightKg for this
                                product code, based on the order qty + UOM. */}
                            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, width: "16%" }}>Product Code</th>
                                        <th style={th}>Description</th>
                                        <th style={{ ...th, width: "13%", textAlign: "center", background: "#e0f2fe" }}>Qty in M</th>
                                        <th style={{ ...th, width: "13%", textAlign: "center", background: "#e0f2fe" }}>Qty in Kg</th>
                                        <th style={{ ...th, width: "13%", textAlign: "right", background: "#fef3c7" }}>Upper Tol. (%)</th>
                                        <th style={{ ...th, width: "13%", textAlign: "right", background: "#fef3c7" }}>Under Tol. (%)</th>
                                        <th style={{ ...th, width: "13%", textAlign: "right", background: "#fffbeb", minHeight: 30 }}>Produced</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ ...td }}>{header.header_product_code || header.product_code || ""}</td>
                                        <td style={td}>{header.productDescription || header.product_description || ""}</td>
                                        <td style={{ ...td, textAlign: "center" }}>
                                            {header.qty_in_m !== undefined && header.qty_in_m !== null && header.qty_in_m !== "" ? header.qty_in_m : ""}
                                        </td>
                                        <td style={{ ...td, textAlign: "center" }}>
                                            {header.qty_in_kg !== undefined && header.qty_in_kg !== null && header.qty_in_kg !== "" ? header.qty_in_kg : ""}
                                        </td>
                                        <td style={{ ...td, textAlign: "right" }}>
                                            {header.upper_tolerance_percent !== undefined && header.upper_tolerance_percent !== null && header.upper_tolerance_percent !== "" ? `+${header.upper_tolerance_percent}%` : ""}
                                        </td>
                                        <td style={{ ...td, textAlign: "right" }}>
                                            {header.under_tolerance_percent !== undefined && header.under_tolerance_percent !== null && header.under_tolerance_percent !== "" ? `-${header.under_tolerance_percent}%` : ""}
                                        </td>
                                        <td style={{ ...td, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>
                                            {header.total_produced_qty !== undefined && header.total_produced_qty !== null && header.total_produced_qty !== "" ? header.total_produced_qty : ""}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>

                        {/* ══ pdf-section-machine-time ══ */}
                        <div id={`pdf-section-machine-time-${index}`}>
                            {routingRows.length > 0 && (() => {
                                const mergedRows = Object.values(
                                    routingRows.reduce((acc, row) => {
                                        const key = row.operation_id || row.process_id;
                                        if (!acc[key]) acc[key] = { ...row, activities: [] };
                                        const actMap = new Map(acc[key].activities.map(a => [a.activity_type?.toLowerCase(), a]));
                                        const pick = (newVal, oldVal) => (newVal !== undefined && newVal !== null && newVal !== "") ? newVal : oldVal;
                                        (row.activities || []).forEach(a => {
                                            const k = (a.activity_type || "").toLowerCase();
                                            const prev = actMap.get(k);
                                            if (!prev) { actMap.set(k, a); return; }
                                            // Merge instead of overwrite: a later duplicate row for the
                                            // same activity_type (e.g. an actual/confirmation row with no
                                            // planned value) must not blank out a good value already found
                                            // (was causing planned Run Time to show 00:00 / vanish).
                                            actMap.set(k, {
                                                ...prev,
                                                ...a,
                                                activity_value: pick(a.activity_value, prev.activity_value),
                                                activity_unit: pick(a.activity_unit, prev.activity_unit),
                                                actual_activity_value: pick(a.actual_activity_value, prev.actual_activity_value),
                                                actual_start_date: pick(a.actual_start_date, prev.actual_start_date),
                                                actual_start_time: pick(a.actual_start_time, prev.actual_start_time),
                                                actual_end_date: pick(a.actual_end_date, prev.actual_end_date),
                                                actual_end_time: pick(a.actual_end_time, prev.actual_end_time),
                                                idle_time_flag: pick(a.idle_time_flag, prev.idle_time_flag),
                                                idle_time_reason: pick(a.idle_time_reason, prev.idle_time_reason),
                                                idle_time_value: pick(a.idle_time_value, prev.idle_time_value),
                                            });
                                        });
                                        acc[key].activities = Array.from(actMap.values());
                                        return acc;
                                    }, {})
                                );

                                const getAct = (row, type) => (row.activities || []).find(a => (a.activity_type || "").toLowerCase() === type.toLowerCase());

                                // Setup/Machine/Labour activity_value is stored as a PER-UNIT rate
                                // (per 1 base_unit), same as the Production Order screen's fmtActValue().
                                // Setup is a fixed duration and is NOT scaled; Machine (Run Time) and
                                // Labour must be multiplied by qty / base_unit to get the real total —
                                // this was missing here, so a 0.13 min/kg rate on 128.52 kg was being
                                // shown as 0.13 min (-> "00:00") instead of the true 16.71 min total.
                                const bUnit = Number(header.base_unit) || 1;
                                const prodQty = Number(header.quantity ?? header.qty_in_kg) || 1;
                                const scaledActivityValue = (act) => {
                                    if (!act || act.activity_value === undefined || act.activity_value === null || act.activity_value === "") return null;
                                    const raw = parseFloat(act.activity_value);
                                    if (isNaN(raw)) return null;
                                    const type = (act.activity_type || "").toLowerCase();
                                    return type === "setup" ? raw : (raw * prodQty) / bUnit;
                                };

                                // Same convention as the Confirmation screen: raw minutes are
                                // converted to a proper "HH:MM" clock string (dividing/carrying
                                // by 60), e.g. 263.74 raw minutes -> "04:24", NOT "263:74".
                                const formatDurationDisplay = (mins) => {
                                    if (mins === undefined || mins === null || mins === "") return "";
                                    const val = parseFloat(mins);
                                    if (isNaN(val)) return "";
                                    const isNeg = val < 0;
                                    const absVal = Math.abs(val);
                                    let h = Math.floor(absVal / 60);
                                    let m = Math.round(absVal % 60);
                                    if (m === 60) { h += 1; m = 0; }
                                    return `${isNeg ? "-" : ""}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                                };

                                // Planned Setup / Run Time schedule — same logic as the Production
                                // Order screen: Setup starts at the operation's Start Date/Time,
                                // Run Time starts when Setup ends. Durations are the same scaled
                                // values shown in the Setup / Run Time columns.
                                const getPlannedSchedule = (row) => {
                                    const blank = { setup: null, run: null };
                                    if (!row.start_date) return blank;
                                    const pad = (n) => String(n).padStart(2, "0");
                                    const minsOf = (act) => {
                                        const v = scaledActivityValue(act);
                                        if (v === null) return 0;
                                        const u = (act.activity_unit || "").toLowerCase();
                                        return (u === "hr" || u === "hrs" || u === "hour" || u === "hours") ? v * 60 : v;
                                    };
                                    const addMins = (d, t, m) => {
                                        const st = new Date(`${d}T${t}`);
                                        if (isNaN(st.getTime())) return null;
                                        const en = new Date(st.getTime() + m * 60000);
                                        return {
                                            date: `${en.getFullYear()}-${pad(en.getMonth() + 1)}-${pad(en.getDate())}`,
                                            time: `${pad(en.getHours())}:${pad(en.getMinutes())}`,
                                        };
                                    };
                                    let curDate = String(row.start_date).substring(0, 10);
                                    let curTime = row.start_time ? String(row.start_time).substring(0, 5) : "00:00";
                                    const out = { setup: null, run: null };
                                    const sAct = getAct(row, "setup");
                                    if (sAct && scaledActivityValue(sAct) !== null) {
                                        const end = addMins(curDate, curTime, minsOf(sAct));
                                        if (end) {
                                            out.setup = { sd: curDate, st: curTime, ed: end.date, et: end.time };
                                            curDate = end.date; curTime = end.time;
                                        }
                                    }
                                    const rAct = getAct(row, "machine");
                                    if (rAct && scaledActivityValue(rAct) !== null) {
                                        const end = addMins(curDate, curTime, minsOf(rAct));
                                        if (end) out.run = { sd: curDate, st: curTime, ed: end.date, et: end.time };
                                    }
                                    return out;
                                };

                                const hasSetup = mergedRows.some(r => { const a = getAct(r, "setup"); return a && a.activity_value != null && a.activity_value !== ""; });
                                const hasRunTime = mergedRows.some(r => { const a = getAct(r, "machine"); return a && a.activity_value != null && a.activity_value !== ""; });
                                const showUom = hasSetup || hasRunTime;

                                // Idle Time — recorded against the Run Time ("machine") activity only.
                                // idle_time_flag: "Yes"/"No", idle_time_reason: free text,
                                // idle_time_value: minutes idle (only meaningful when flag === "Yes").
                                // Columns always render alongside Operator/QC (whenever Run Time exists),
                                // same as the Confirmation screen — not gated on data already being present.

                                const thWrap = { ...th, whiteSpace: "normal", wordBreak: "break-word" };

                                // PLANNED block: same 8-column, multi-row-per-process layout as
                                // the Actual block — Setup dates, Run Time dates, then the overall
                                // Start/End (nothing from the old planned table is dropped).
                                const plannedColHeadersRow1 = (
                                    <tr>
                                        <th style={thWrap}>Process</th>
                                        <th style={thWrap}>Workcenter</th>
                                        <th style={thWrap}>Machine</th>
                                        <th style={thWrap}>Setup Start Date</th>
                                        <th style={thWrap}>Setup Start Time</th>
                                        <th style={thWrap}>Setup End Date</th>
                                        <th style={thWrap}>Setup End Time</th>
                                        <th style={{ ...thWrap, textAlign: "right" }}>Setup (HH:MM)</th>
                                    </tr>
                                );
                                const plannedColHeadersRow2 = (
                                    <tr>
                                        <th style={thWrap}>Run Time Start Date</th>
                                        <th style={thWrap}>Run Time Start Time</th>
                                        <th style={thWrap}>Run Time End Date</th>
                                        <th style={thWrap}>Run Time End Time</th>
                                        <th style={{ ...thWrap, textAlign: "right" }}>Run Time (HH:MM)</th>
                                        <th style={thWrap}>UOM</th>
                                        <th style={thWrap}>Created By</th>
                                        <th style={thWrap}>Released By</th>
                                    </tr>
                                );
                                const plannedColHeadersRow3 = (
                                    <tr>
                                        <th style={thWrap} colSpan={2}>Start Date</th>
                                        <th style={thWrap} colSpan={2}>Start Time</th>
                                        <th style={thWrap} colSpan={2}>End Date</th>
                                        <th style={thWrap} colSpan={2}>End Time</th>
                                    </tr>
                                );

                                // ACTUAL block: exactly the 16 requested columns, in the requested
                                // order, split across a fixed 8-column table as two physical rows
                                // per process — row 1 = Process..Setup, row 2 = Run Time..Idle Reason
                                // (each cell in row 2 carries a small label so its column meaning is
                                // clear even though it reuses row 1's 8 columns).
                                const actualColHeadersRow1 = (
                                    <tr>
                                        <th style={thWrap}>Process</th>
                                        <th style={thWrap}>Workcenter</th>
                                        <th style={thWrap}>Machine</th>
                                        <th style={thWrap}>Setup Start Date</th>
                                        <th style={thWrap}>Setup Start Time</th>
                                        <th style={thWrap}>Setup End Date</th>
                                        <th style={thWrap}>Setup End Time</th>
                                        <th style={{ ...thWrap, textAlign: "right" }}>Setup (HH:MM)</th>
                                    </tr>
                                );
                                const actualColHeadersRow2 = (
                                    <tr>
                                        <th style={thWrap}>Run Time Start Date</th>
                                        <th style={thWrap}>Run Time Start Time</th>
                                        <th style={thWrap}>Run Time End Date</th>
                                        <th style={thWrap}>Run Time End Time</th>
                                        <th style={{ ...thWrap, textAlign: "right" }}>Run Time (HH:MM)</th>
                                        <th style={thWrap}>Idle Time</th>
                                        <th style={{ ...thWrap, textAlign: "right" }}>Idle Time (HH:MM)</th>
                                        <th style={thWrap}>Idle Reason</th>
                                    </tr>
                                );

                                const renderBlock = (isActual) => {
                                    if (!isActual) {
                                        return (
                                            <div>
                                                <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 13, color: "#1e3a5f", background: "#dbeafe", padding: "5px 12px", letterSpacing: "0.06em", textTransform: "uppercase" }}>Planned</div>
                                                <div>
                                                    {mergedRows.map((row, idx) => {
                                                        const cellStyle = { ...td, whiteSpace: "normal", wordBreak: "break-word", minHeight: 30 };
                                                        const setupAct = getAct(row, "setup");
                                                        const machineAct = getAct(row, "machine");
                                                        const sharedUom = (setupAct || machineAct)?.activity_unit || "Min";
                                                        const rowBg = idx % 2 === 0 ? "#fff" : "#fafbfc";
                                                        const sched = getPlannedSchedule(row);
                                                        return (
                                                            <table key={idx} style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", marginBottom: idx === mergedRows.length - 1 ? 0 : 4 }}>
                                                                <thead>{plannedColHeadersRow1}</thead>
                                                                <tbody>
                                                                    <tr style={{ background: rowBg }}>
                                                                        <td style={cellStyle}>{row.process_name || ""}</td>
                                                                        <td style={cellStyle}>{row.workcenter_name || ""}</td>
                                                                        <td style={cellStyle}>{row.machine || ""}</td>
                                                                        <td style={cellStyle}>{sched.setup ? fmtDate(sched.setup.sd) : ""}</td>
                                                                        <td style={cellStyle}>{sched.setup ? fmtTime(sched.setup.st) : ""}</td>
                                                                        <td style={cellStyle}>{sched.setup ? fmtDate(sched.setup.ed) : ""}</td>
                                                                        <td style={cellStyle}>{sched.setup ? fmtTime(sched.setup.et) : ""}</td>
                                                                        <td style={{ ...cellStyle, textAlign: "right" }}>{setupAct ? formatDurationDisplay(scaledActivityValue(setupAct)) : ""}</td>
                                                                    </tr>
                                                                </tbody>
                                                                <thead>{plannedColHeadersRow2}</thead>
                                                                <tbody>
                                                                    <tr style={{ background: rowBg }}>
                                                                        <td style={cellStyle}>{sched.run ? fmtDate(sched.run.sd) : ""}</td>
                                                                        <td style={cellStyle}>{sched.run ? fmtTime(sched.run.st) : ""}</td>
                                                                        <td style={cellStyle}>{sched.run ? fmtDate(sched.run.ed) : ""}</td>
                                                                        <td style={cellStyle}>{sched.run ? fmtTime(sched.run.et) : ""}</td>
                                                                        <td style={{ ...cellStyle, textAlign: "right" }}>{machineAct ? formatDurationDisplay(scaledActivityValue(machineAct)) : ""}</td>
                                                                        <td style={{ ...cellStyle, color: "#64748b" }}>{showUom ? sharedUom : ""}</td>
                                                                        <td style={cellStyle}>{row.created_by || header.created_by || ""}</td>
                                                                        <td style={cellStyle}>{row.released_by || header.released_by || ""}</td>
                                                                    </tr>
                                                                </tbody>
                                                                <thead>{plannedColHeadersRow3}</thead>
                                                                <tbody>
                                                                    <tr style={{ background: rowBg, borderBottom: "2px solid #000" }}>
                                                                        <td style={cellStyle} colSpan={2}>{row.start_date ? fmtDate(row.start_date) : ""}</td>
                                                                        <td style={cellStyle} colSpan={2}>{row.start_time ? fmtTime(row.start_time) : ""}</td>
                                                                        <td style={cellStyle} colSpan={2}>{row.end_date ? fmtDate(row.end_date) : ""}</td>
                                                                        <td style={cellStyle} colSpan={2}>{row.end_time ? fmtTime(row.end_time) : ""}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div>
                                            <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 13, color: "#0f766e", background: "#dcfce7", padding: "5px 12px", letterSpacing: "0.06em", textTransform: "uppercase", borderTop: "2px solid #000" }}>Actual</div>
                                            <div>
                                                {mergedRows.map((row, idx) => {
                                                    const cellStyle = { ...td, whiteSpace: "normal", wordBreak: "break-word", minHeight: 30 };
                                                    const setupAct = getAct(row, "setup");
                                                    const machineAct = getAct(row, "machine");
                                                    const rowBg = idx % 2 === 0 ? "#fff" : "#fafbfc";
                                                    const idleFlag = machineAct?.idle_time_flag || "";
                                                    const idleValue = (machineAct?.idle_time_flag === "Yes" && machineAct?.idle_time_value != null && machineAct?.idle_time_value !== "")
                                                        ? machineAct.idle_time_value
                                                        : "";
                                                    const idleReason = machineAct?.idle_time_flag === "Yes" ? (machineAct?.idle_time_reason || "") : "";

                                                    return (
                                                        <table key={idx} style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", marginBottom: idx === mergedRows.length - 1 ? 0 : 4 }}>
                                                            <thead>{actualColHeadersRow1}</thead>
                                                            <tbody>
                                                                {/* Row 1: Process, Workcenter, Machine, Setup Start Date, Setup Start Time, Setup End Date, Setup End Time, Setup */}
                                                                <tr style={{ background: rowBg }}>
                                                                    <td style={cellStyle}>{row.process_name || ""}</td>
                                                                    <td style={cellStyle}>{row.workcenter_name || ""}</td>
                                                                    <td style={cellStyle}>{row.machine || ""}</td>
                                                                    <td style={cellStyle}>{setupAct?.actual_start_date ? fmtDate(setupAct.actual_start_date) : ""}</td>
                                                                    <td style={cellStyle}>{setupAct?.actual_start_time ? fmtTime(setupAct.actual_start_time) : ""}</td>
                                                                    <td style={cellStyle}>{setupAct?.actual_end_date ? fmtDate(setupAct.actual_end_date) : ""}</td>
                                                                    <td style={cellStyle}>{setupAct?.actual_end_time ? fmtTime(setupAct.actual_end_time) : ""}</td>
                                                                    <td style={{ ...cellStyle, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>{formatDurationDisplay(setupAct?.actual_activity_value)}</td>
                                                                </tr>
                                                            </tbody>
                                                            <thead>{actualColHeadersRow2}</thead>
                                                            <tbody>
                                                                {/* Row 2: Run Time Start Date, Run Time Start Time, Run Time End Date, Run Time End Time, Run Time, Idle Time, Idle Mins, Idle Reason */}
                                                                <tr style={{ background: rowBg, borderBottom: "2px solid #000" }}>
                                                                    <td style={cellStyle}>{machineAct?.actual_start_date ? fmtDate(machineAct.actual_start_date) : ""}</td>
                                                                    <td style={cellStyle}>{machineAct?.actual_start_time ? fmtTime(machineAct.actual_start_time) : ""}</td>
                                                                    <td style={cellStyle}>{machineAct?.actual_end_date ? fmtDate(machineAct.actual_end_date) : ""}</td>
                                                                    <td style={cellStyle}>{machineAct?.actual_end_time ? fmtTime(machineAct.actual_end_time) : ""}</td>
                                                                    <td style={{ ...cellStyle, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>{formatDurationDisplay(machineAct?.actual_activity_value)}</td>
                                                                    <td style={cellStyle}>{idleFlag}</td>
                                                                    <td style={{ ...cellStyle, textAlign: "right" }}>{idleValue !== "" ? formatDurationDisplay(idleValue) : ""}</td>
                                                                    <td style={cellStyle}>{idleReason}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        <SectionTitle num="1" title="Machine Time" />
                                        <div style={{ marginBottom: 16, border: "1px solid #000" }}>
                                            {renderBlock(false)}
                                            {renderBlock(true)}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* ══ pdf-section-material ══ */}
                        <div id={`pdf-section-material-${index}`}>
                            <SectionTitle num="2" title="Material Details" />
                            {bomRows.length > 0 && (
                                <>
                                    <div style={{ fontFamily: CAL, fontSize: 13, marginBottom: 6, color: "#1e293b", padding: "4px 10px", background: "#e8f0fe", borderLeft: "3px solid #2563eb" }}>Input</div>
                                    <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...th, width: 36, textAlign: "center" }}>Item</th>
                                                    <th style={th}>Product Type</th>
                                                    <th style={th}>Product Code</th>
                                                    <th style={th}>Description</th>
                                                    <th style={th}>UOM</th>
                                                    <th style={{ ...th, textAlign: "right" }}>Total Qty</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Consumed</th>
                                                    {scrapCols.map(c => (
                                                        <th key={c.key} style={{ ...th, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>{c.label}</th>
                                                    ))}
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Storage Location</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Batch Number</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bomRows.map((row, idx) => {
                                                    const attrs = (componentAttributes || {})[row.product_code] || [];
                                                    // Batch Number — prefer the ACTUAL batch consumed at confirmation
                                                    // time (row.component_batch / row.batches). If the order hasn't
                                                    // been confirmed yet (or this component wasn't in that
                                                    // confirmation), fall back to the PLANNED/selected batch number
                                                    // stored directly on po_material_details (row.batch_number) —
                                                    // same fallback pattern already used for Storage Location below.
                                                    const batchSource = row.component_batch || row.batch_number || "";
                                                    const batchList = Array.isArray(row.batches) && row.batches.length > 0
                                                        ? row.batches
                                                        : (batchSource
                                                            ? String(batchSource).split(",").filter(Boolean).map(bn => ({ batch_number: bn, qty: null }))
                                                            : []);
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            <tr style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                                <td style={{ ...td, textAlign: "center" }}>{idx + 1}</td>
                                                                <td style={td}>{row.product_type || ""}</td>
                                                                <td style={{ ...td }}>{row.product_code || ""}</td>
                                                                <td style={td}>{row.description || row.product_description || ""}</td>
                                                                <td style={td}>{row.uom || ""}</td>
                                                                <td style={{ ...td, textAlign: "right" }}>{row.total_qty || row.quantity || ""}</td>
                                                                <td style={{ ...td, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>
                                                                    {row.confirm_qty !== undefined && row.confirm_qty !== null && row.confirm_qty !== "" ? row.confirm_qty : ""}
                                                                </td>
                                                                {scrapCols.map(c => (
                                                                    <td key={c.key} style={{ ...td, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>
                                                                        {scrapVal(row, c.key)}
                                                                    </td>
                                                                ))}
                                                                <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>
                                                                    {row.component_storage_bin || row.storage_location || ""}
                                                                </td>
                                                                <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>
                                                                    {batchList.length === 0 ? (
                                                                        ""
                                                                    ) : (
                                                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                            {batchList.map((b, bIdx) => (
                                                                                <span key={bIdx} style={{ fontSize: 11, color: "#1d4ed8", whiteSpace: "nowrap" }}>
                                                                                    {b.batch_number}{b.qty !== null && b.qty !== undefined ? `(${b.qty})` : ""}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {attrs.length > 0 && (
                                                                <tr>
                                                                    <td colSpan={9 + scrapCols.length} style={{ padding: 0, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{ ...th, width: 36, textAlign: "center", fontSize: 11 }}>Sl</th>
                                                                                    <th style={{ ...th, fontSize: 11 }}>Text Parameter</th>
                                                                                    <th style={{ ...th, fontSize: 11 }}>Job TP</th>
                                                                                    <th style={{ ...th, fontSize: 11, background: "#fffbeb", minHeight: 30 }}>Actuals</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {attrs.map((attr, aIdx) => (
                                                                                    <tr key={aIdx}>
                                                                                        <td style={{ ...td, textAlign: "center", fontSize: 11, color: "#94a3b8" }}>{aIdx + 1}</td>
                                                                                        <td style={{ ...td, fontSize: 12 }}>{attr.characteristic}</td>
                                                                                        <td style={{ ...td, fontSize: 12 }}>{attr.value || ""}</td>
                                                                                        <td style={{ ...td, fontSize: 12, background: "#fffbeb", minHeight: 30 }}>{attr.actual_value || ""}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            <div style={{ fontFamily: CAL, fontSize: 13, marginBottom: 6, color: "#1e293b", padding: "4px 10px", background: "#dcfce7", borderLeft: "3px solid #16a34a" }}>Output</div>
                            <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...th, width: 36, textAlign: "center" }}>Item</th>
                                            <th style={th}>Product Type</th>
                                            <th style={th}>Product Code</th>
                                            <th style={th}>Description</th>
                                            <th style={th}>UOM</th>
                                            <th style={{ ...th, textAlign: "right" }}>Total Qty</th>
                                            <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Produced</th>
                                            <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Storage Location</th>
                                            <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Batch Number</th>
                                            <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...td, textAlign: "center" }}>1</td>
                                            <td style={td}>{header.productType || header.product_type || ""}</td>
                                            <td style={{ ...td }}>{header.header_product_code || header.product_code || ""}</td>
                                            <td style={td}>{header.productDescription || header.product_description || ""}</td>
                                            <td style={td}>{header.header_uom || ""}</td>
                                            <td style={{ ...td, textAlign: "right" }}>{header.general_qty || header.quantity || ""}</td>
                                            <td style={{ ...td, textAlign: "right", background: "#fffbeb", minHeight: 30 }}>
                                                {header.total_produced_qty !== undefined && header.total_produced_qty !== null && header.total_produced_qty !== "" ? header.total_produced_qty : ""}
                                            </td>
                                            <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{header.storage_location || ""}</td>
                                            <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{header.batch_no || ""}</td>
                                            <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{header.release_status || ""}</td>
                                        </tr>
                                        {outputAttributes.length > 0 && (
                                            <tr>
                                                <td colSpan={10} style={{ padding: 0, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ ...th, width: 36, textAlign: "center", fontSize: 11 }}>Sl</th>
                                                                <th style={{ ...th, fontSize: 11 }}>Text Parameter</th>
                                                                <th style={{ ...th, fontSize: 11 }}>Job TP</th>
                                                                <th style={{ ...th, fontSize: 11, background: "#fffbeb", minHeight: 30 }}>Actuals</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {outputAttributes.map((attr, aIdx) => (
                                                                <tr key={aIdx}>
                                                                    <td style={{ ...td, textAlign: "center", fontSize: 11, color: "#94a3b8" }}>{aIdx + 1}</td>
                                                                    <td style={{ ...td, fontSize: 12 }}>{attr.characteristic}</td>
                                                                    <td style={{ ...td, fontSize: 12 }}>{attr.value || ""}</td>
                                                                    <td style={{ ...td, fontSize: 12, background: "#fffbeb", minHeight: 30 }}>{attr.actual_value || ""}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Output Scrap — Setup / Process / Wirebreak Scrap.
                                Order-level values (not per-bobbin), kept in their OWN row/table
                                so the wide Output table above never needs horizontal scroll
                                (which breaks on printout). Shown for every process now — Input
                                never carries scrap, it's always captured here at the Output
                                level (same as Confirmation.js). */}
                            <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...th, background: "#fef2f2" }}>Setup Scrap (Kg)</th>
                                            <th style={{ ...th, background: "#fef2f2" }}>Process Scrap (Kg)</th>
                                            <th style={{ ...th, background: "#fef2f2" }}>Wirebreak Scrap (Kg)</th>
                                            <th style={{ ...th, background: "#fef2f2" }}>Total Scrap (Kg)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...td, background: "#fef2f2", minHeight: 30 }}>
                                                {bobbinRows[0]?.setup_scrap ?? ""}
                                            </td>
                                            <td style={{ ...td, background: "#fef2f2", minHeight: 30 }}>
                                                {bobbinRows[0]?.process_scrap ?? ""}
                                            </td>
                                            <td style={{ ...td, background: "#fef2f2", minHeight: 30 }}>
                                                {bobbinRows[0]?.wirebreak_scrap ?? ""}
                                            </td>
                                            <td style={{ ...td, background: "#fef2f2", minHeight: 30 }}>
                                                {(() => {
                                                    const s = bobbinRows[0]?.setup_scrap;
                                                    const p = bobbinRows[0]?.process_scrap;
                                                    const w = bobbinRows[0]?.wirebreak_scrap;
                                                    if (s == null && p == null && w == null) return "";
                                                    const total = (parseFloat(s) || 0) + (parseFloat(p) || 0) + (parseFloat(w) || 0);
                                                    return total.toFixed(4);
                                                })()}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Group-driven Output capture — No. of Bobbins / Length / Weight /
                                Bobbin No, plus Setup/Process/Wirebreak Scrap. Only shown when
                                this production order's process has process_configuration.group_id
                                set (see hasScrapGroupLogic from getJobOrderDetails). */}
                            {hasScrapGroupLogic && (() => {
                                const bobbinColHeaders = (isActual) => (
                                    <tr>
                                        <th style={{ ...th, background: isActual ? "#fffbeb" : undefined }}>No. of Spools/Baskets</th>
                                        <th style={{ ...th, background: isActual ? "#fffbeb" : undefined }}>Length (M) per Spools/Baskets</th>
                                        <th style={{ ...th, background: isActual ? "#fffbeb" : undefined }}>Weight (KG) per Spools/Baskets</th>
                                        <th style={{ ...th, background: isActual ? "#fffbeb" : undefined }}>Spools/Baskets Name</th>
                                    </tr>
                                );

                                const renderBobbinBlock = (isActual, rows) => (
                                    <div>
                                        <div style={{ fontFamily: "Calibri, sans-serif", fontSize: 13, color: isActual ? "#0f766e" : "#1e3a5f", background: isActual ? "#dcfce7" : "#dbeafe", padding: "5px 12px", letterSpacing: "0.06em", textTransform: "uppercase", borderTop: isActual ? "2px solid #000" : "none" }}>{isActual ? "Actual" : "Planned"}</div>
                                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                            <thead>{bobbinColHeaders(isActual)}</thead>
                                            <tbody>
                                                {rows.length > 0 ? (
                                                    rows.map((row, bIdx) => (
                                                        <tr key={bIdx} style={{ background: isActual ? undefined : (bIdx % 2 === 0 ? "#fff" : "#fafbfc") }}>
                                                            <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}>
                                                                {row?.no_of_bobbins ?? ""}
                                                            </td>
                                                            <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}>
                                                                {row?.length ?? ""}
                                                            </td>
                                                            <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}>
                                                                {row?.weight ?? ""}
                                                            </td>
                                                            <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}>
                                                                {row?.bobbin_no ?? ""}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}></td>
                                                        <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}></td>
                                                        <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}></td>
                                                        <td style={{ ...td, background: isActual ? "#fffbeb" : undefined, minHeight: 30 }}></td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                );

                                return (
                                    <div style={{ marginBottom: 16, border: "1px solid #000" }}>
                                        {renderBobbinBlock(false, plannedBobbinRows)}
                                        {renderBobbinBlock(true, displayBobbinRows)}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* ══ pdf-section-process ══ */}
                        <div id={`pdf-section-process-${index}`}>
                            {processParameters.length > 0 && (
                                <>
                                    <SectionTitle num="3" title="Process Parameters" />
                                    <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...th, width: 36, textAlign: "center" }}>Sl</th>
                                                    <th style={th}>Process</th>
                                                    {processParameters.some((r) => r.sub_process_name) && <th style={th}>Sub Process</th>}
                                                    <th style={th}>Parameter</th>
                                                    <th style={th}>UOM</th>
                                                    <th style={th}>Specified Value</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Actual Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {processParameters.map((row, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                        <td style={{ ...td, textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                                                        <td style={td}>{row.process_name || ""}</td>
                                                        {processParameters.some((r) => r.sub_process_name) && <td style={td}>{row.sub_process_name || ""}</td>}
                                                        <td style={{ ...td }}>{row.parameter_name || ""}</td>
                                                        <td style={{ ...td, color: "#64748b" }}>{row.parameter_uom || ""}</td>
                                                        <td style={{ ...td }}>{row.parameter_value ?? ""}</td>
                                                        <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{row.actual_value !== undefined && row.actual_value !== null && row.actual_value !== "" ? row.actual_value : ""}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ══ pdf-section-machine-params ══ */}
                        <div id={`pdf-section-machine-params-${index}`}>
                            {machineParameters.length > 0 && (
                                <>
                                    <SectionTitle num="4" title="Machine Parameters" />
                                    <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...th, width: 36, textAlign: "center" }}>Sl</th>
                                                    <th style={th}>Process</th>
                                                    {machineParameters.some((r) => r.sub_process_name) && <th style={th}>Sub Process</th>}
                                                    <th style={th}>Workcenter</th>
                                                    <th style={th}>Parameter</th>
                                                    <th style={th}>UOM</th>
                                                    <th style={th}>Specified Value</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Actual Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {machineParameters.map((row, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                        <td style={{ ...td, textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                                                        <td style={td}>{row.process_name || ""}</td>
                                                        {machineParameters.some((r) => r.sub_process_name) && <td style={td}>{row.sub_process_name || ""}</td>}
                                                        <td style={{ ...td }}>{row.workcenter_name || ""}</td>
                                                        <td style={td}>{row.parameter_name || ""}</td>
                                                        <td style={{ ...td, color: "#64748b" }}>{row.parameter_uom || ""}</td>
                                                        <td style={{ ...td }}>{row.parameter_value ?? ""}</td>
                                                        <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{row.actual_value !== undefined && row.actual_value !== null && row.actual_value !== "" ? row.actual_value : ""}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ══ pdf-section-inspection ══ */}
                        <div id={`pdf-section-inspection-${index}`}>
                            {inspectionParameters.length > 0 && (
                                <>
                                    <SectionTitle num="5" title="Inspection Parameters" />
                                    <div style={{ overflowX: "auto", marginBottom: 16 }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...th, width: 36, textAlign: "center" }}>Sl</th>
                                                    <th style={th}>Process</th>
                                                    {inspectionParameters.some((r) => r.sub_process_name) && <th style={th}>Sub Process</th>}
                                                    <th style={th}>Test Parameter</th>
                                                    <th style={th}>UOM</th>
                                                    <th style={th}>Specified Value</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Actual Value</th>
                                                    <th style={{ ...th, background: "#fffbeb", minHeight: 30 }}>Checked</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {inspectionParameters.map((ip, idx) => (
                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                        <td style={{ ...td, textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                                                        <td style={td}>{ip.process_name || ""}</td>
                                                        {inspectionParameters.some((r) => r.sub_process_name) && <td style={td}>{ip.sub_process_name || ""}</td>}
                                                        <td style={{ ...td }}>{ip.characteristic || ""}</td>
                                                        <td style={{ ...td, color: "#64748b" }}>{ip.uom || ""}</td>
                                                        <td style={{ ...td }}>{ip.standard_value || ""}</td>
                                                        <td style={{ ...td, background: "#fffbeb", minHeight: 30 }}>{ip.actual_value !== undefined && ip.actual_value !== null && ip.actual_value !== "" ? ip.actual_value : ""}</td>
                                                        <td style={{
                                                            ...td,
                                                            background: ip.checked === "Passed" ? "#f0fdf4" : ip.checked === "NC" ? "#fef2f2" : "#fffbeb",
                                                            color: ip.checked === "Passed" ? "#16a34a" : ip.checked === "NC" ? "#dc2626" : "#64748b",
                                                            }}>
                                                            {ip.checked || ""}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                        </div>

                        {/* ══ pdf-section-special-remarks ══
                            Optional free text typed on the Planned Order. Hidden when empty,
                            same as the Process / Machine / Inspection Parameter sections. */}
                        {!!String(specialRemarks || "").trim() && (
                            <div id={`pdf-section-special-remarks-${index}`}>
                                <SectionTitle num="6" title="Special Remarks" />
                                <div style={{
                                    border: "1px solid #000", padding: "10px 14px", marginBottom: 16,
                                    fontFamily: CAL, fontSize: 13, color: "#111",
                                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                                }}>
                                    {specialRemarks}
                                </div>
                            </div>
                        )}

                        {/* ══ pdf-section-downtime ══ */}
                        <div id={`pdf-section-downtime-${index}`}>
                                {/* Downtime Codes reference box — matches the physical Job Card's
                                    printed note (Format No. F/PRD/04) listing codes 01–21. */}
                                <div style={{ border: "1px solid #000", marginBottom: 16, fontFamily: TNR, fontSize: 10.5, color: "#333" }}>
                                    <div style={{ display: "flex" }}>
                                        <div style={{ width: 130, fontWeight: 700, padding: "8px 10px", borderRight: "1px solid #000", flexShrink: 0 }}>
                                            Downtime Codes
                                        </div>
                                        <div style={{ padding: "8px 10px", lineHeight: 1.6 }}>
                                            {[
                                                "01 Electrical Breakdown",
                                                "02 Mechanical Breakdown",
                                                "03 No Material",
                                                "04 No Power",
                                                "05 Preventive Maintenance",
                                                "06 Power Failure effect",
                                                "07 Lunch Time",
                                                "08 No Man Power",
                                                "09 Other Problems",
                                                "10 Colour Change",
                                                "11 Setup time",
                                                "12 Preperation time",
                                                "13 Loading / Unloading Time",
                                                "14 Machine & Floor Clean",
                                                "15 Reporting Scrap",
                                                "16 Rework",
                                                "17 No fork Lift / Trolley / Pallet truck",
                                                "18 No work",
                                                "19 Wire / Sheath Break",
                                                "20 Meeting",
                                                "21 Running Hours",
                                            ].join(",  ")}
                                        </div>
                                    </div>
                                </div>
                        </div>

                        {/* ══ pdf-section-footer ══ */}
                        <div id={`pdf-section-footer-${index}`}>
                            <div style={{ borderTop: "2px solid #000", paddingTop: 8, display: "flex", justifyContent: "space-between", fontFamily: TNR, fontSize: 9, color: "#333" }}>
                                <span>SBEE — Confidential — Internal Use Only</span>
                                <span>Job Order: {header.production_order_number}</span>
                                <span>Printed: {new Date().toLocaleString("en-IN")}</span>
                            </div>
                        </div>
                    </div>
                );
            })}

            {/* ── Print CSS ── */}
            <style>{`
                /* Ensure page breaks between job orders */
                .job-order-page {
                    page-break-after: always;
                    break-after: page;
                }
                .job-order-page:last-child {
                    page-break-after: auto;
                    break-after: auto;
                }
                
                @media print {
                    .no-print { display: none !important; }
                    body { background: #fff !important; }
                    .job-order-page {
                        border: 8px solid #000 !important;
                        max-width: 100% !important;
                        margin: 0 0 20px 0 !important;
                        padding: 20px 28px !important;
                        box-shadow: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default JobOrder;